import { FC, useEffect, useState } from 'react'
import { Label } from '@/components/ui/label.tsx'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx'
import { HexColorInput, RgbaStringColorPicker } from 'react-colorful'
import { Button } from '@/components/ui/button.tsx'
import { Undo2 } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.tsx'

type ColorPickerProps = {
    label: string
    labelStyle?: string
    value: string
    onChange: (color: string) => void
    zIndex?: number
    defaultColor?: string
}

export const ColorPicker: FC<ColorPickerProps> = ({
    label,
    labelStyle,
    value,
    onChange,
    zIndex = 2000,
    defaultColor = undefined,
}) => {
    const [color, setColor] = useState<string | null>(value)

    useEffect(() => {
        setColor(value)
    }, [value])

    return (
        <div className={'flex flex-row justify-between items-center w-full'}>
            <Label htmlFor={'color'} className={`flex-1 ${labelStyle ?? ''}`}>
                {label}
            </Label>
            <div className={'flex flex-row items-center gap-3'}>
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant={'outline'} className={'w-11 h-8 p-1.5 cursor-pointer'}>
                            <div
                                className={'w-full h-full rounded-sm ring ring-mm-border'}
                                style={{ backgroundColor: color }}
                            />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className={`w-fit z-[${zIndex}] p-1`}>
                        <RgbaStringColorPicker
                            color={color}
                            onChange={(newColor) => {
                                setColor(newColor)
                                onChange(newColor)
                            }}
                        />
                        <div className={'flex-1 text-sm m-2'}>
                            <Label htmlFor={'color'} className={'pr-1 text-mm-gray2'}>
                                HEX:
                            </Label>
                            <HexColorInput
                                color={color}
                                onChange={(newColor) => {
                                    setColor(newColor)
                                    onChange(newColor)
                                }}
                                className={'w-24'}
                                alpha={true}
                                prefixed
                            />
                        </div>
                    </PopoverContent>
                </Popover>
                {defaultColor && (
                    <div className={'flex items-center m-1'}>
                        <Tooltip delayDuration={100}>
                            <TooltipTrigger>
                                <Undo2
                                    size={16}
                                    className="hover:text-mm-gray text-mm-gray2"
                                    onClick={() => {
                                        setColor(defaultColor)
                                        onChange(null)
                                    }}
                                />
                            </TooltipTrigger>
                            <TooltipContent align={'end'}>zurücksetzen</TooltipContent>
                        </Tooltip>
                    </div>
                )}
            </div>
        </div>
    )
}
